// src/pages/ChefDemo.js
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import "./ChefDemo.css";

// Cloudflare Images URLs for placeholder and dish images
const PlaceholderChef = "https://imagedelivery.net/YQhisQdXT8651Bk9Z7c9KQ/b459f40c-612e-478e-2210-e2ec4e600400/public";

// Demo dish images (food): chefs 6..10 from Cloudflare Images
const DishImg1 = "https://imagedelivery.net/YQhisQdXT8651Bk9Z7c9KQ/58756b2a-9dc0-4097-9fb0-c3217f80dd00/public"; // chef6
const DishImg2 = "https://imagedelivery.net/YQhisQdXT8651Bk9Z7c9KQ/f3134b26-c016-4b9a-2809-8d8e332ba400/public"; // chef7
const DishImg3 = "https://imagedelivery.net/YQhisQdXT8651Bk9Z7c9KQ/b2e3b402-3660-4e3e-23aa-cfb693b16d00/public"; // chef8
const DishImg4 = "https://imagedelivery.net/YQhisQdXT8651Bk9Z7c9KQ/dc110e42-bc52-48d2-a47b-cc46b5ccd700/public"; // chef9
const DishImg5 = "https://imagedelivery.net/YQhisQdXT8651Bk9Z7c9KQ/73c31bd5-c860-44e3-4827-133779e10a00/public"; // chef10

const DEMO_DISH_IMAGES = [DishImg1, DishImg2, DishImg3, DishImg4, DishImg5];

function pickRandom(arr) { return Array.isArray(arr) && arr.length ? arr[Math.floor(Math.random() * arr.length)] : null; }
function stripQuotes(s) { return typeof s === "string" ? s.replace(/^["']|["']$/g, "") : s; }
function firstNonEmpty(...vals) { for (const v of vals) { if (v != null && String(v).trim() !== "") return v; } return null; }

function normalizeSpecialties(value) {
  if (value == null) return [];
  if (Array.isArray(value)) return value.map(stripQuotes).filter(Boolean);
  if (typeof value === "string") {
    const s = value.trim();
    if (s.startsWith("[") && s.endsWith("]")) {
      try {
        const parsed = JSON.parse(s);
        return Array.isArray(parsed) ? parsed.map(stripQuotes).filter(Boolean) : [stripQuotes(s)];
      } catch {}
    }
    if (s.includes(",")) return s.split(",").map((t) => stripQuotes(t.trim())).filter(Boolean);
    return [stripQuotes(s)];
  }
  return [];
}

function buildDemoDishes(chefName = "Local Chef", city = "your city") {
  const names = ["Signature Plate","Seasonal Special","Chef’s Favorite","Market Bowl","Tasting Dish"];
  const descs = [
    `A showcase from ${chefName}, highlighting peak flavors in ${city}.`,
    "Balanced, bright, and satisfying—crafted fresh daily.",
    "Comfort with a modern twist, prepared with care.",
    "Locally sourced ingredients, simply done right.",
    "A playful composition of textures and aromas."
  ];
  const prices = [1299,1499,1599,1799,1999];

  return DEMO_DISH_IMAGES.map((img, i) => ({
    id: `demo-${i + 1}`,
    name: names[i % names.length],
    description: descs[i % descs.length],
    price_cents: prices[i % prices.length],
    image_url: img
  }));
}

export default function ChefDemo() {
  const navigate = useNavigate();
  const { city: cityParam } = useParams();
  const city = (cityParam || "toronto").toLowerCase();

  // Read prefill from the URL so the clicked card's identity is preserved.
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const idParam   = params.get("id");
  const nameParam = params.get("name");
  const photoParam= params.get("photo");
  const specsParam= params.get("specialties");
  const dataSourceParam = params.get("data_source");

  const prefillChef = useMemo(() => {
    if (idParam || nameParam || photoParam || specsParam) {
      return {
        id: idParam || undefined,
        name: nameParam || undefined,
        photo_url: photoParam || undefined,
        specialties: specsParam || undefined,
        city,
        status: "approved",
        data_source: dataSourceParam || "demo",
      };
    }
    return null;
  }, [idParam, nameParam, photoParam, specsParam, dataSourceParam, city]);

  const [chef, setChef] = useState(prefillChef);
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dishLoading, setDishLoading] = useState(true);
  const [hardError, setHardError] = useState(null); // only show for 5xx or network failures

  // Load/resolve the chef. Gracefully handle 4xx (e.g., 400) by falling back to prefill/demo.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setHardError(null);

    (async () => {
      try {
        const res = await fetch(`/api/chefs?city=${encodeURIComponent(city)}&limit=100`, {
          headers: { accept: "application/json" },
        });

        let list = [];
        if (res.ok) {
          const json = await res.json();
          list = Array.isArray(json?.chefs) ? json.chefs : [];
        } else {
          // Swallow 4xx (like 400) silently; only flag 5xx as a hard error.
          console.warn(`Chefs API returned ${res.status}`);
          if (res.status >= 500) setHardError(`Chefs API ${res.status}`);
          list = [];
        }

        const approved = list.filter((c) => (c.status || "approved") === "approved");
        let chosen = null;

        if (idParam) {
          chosen = (approved.length ? approved : list).find((c) => String(c.id) === String(idParam)) || null;
        }
        if (!chosen) chosen = prefillChef || pickRandom(approved.length ? approved : list);

        if (!cancelled) setChef(chosen || prefillChef || null);
      } catch (e) {
        console.warn("Chefs API fetch failed", e);
        if (!cancelled) {
          setChef(prefillChef || null);
          setHardError("Chefs API unavailable");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [city, idParam, prefillChef]);

  // Load dishes for the chosen chef; fallback to demo dishes (food photos 6..10).
  useEffect(() => {
    let cancelled = false;

    // If no real ID (local/demo card), just show demo dishes.
    if (!chef?.id) {
      setDishes(buildDemoDishes(chef?.name || "Local Chef", city));
      setDishLoading(false);
      return;
    }

    setDishLoading(true);
    (async () => {
      try {
        const res = await fetch(`/api/dishes?city=${encodeURIComponent(city)}&limit=100`, {
          headers: { accept: "application/json" },
        });

        let items = [];
        if (res.ok) {
          const json = await res.json();
          items = Array.isArray(json?.dishes) ? json.dishes : [];
          
          // Parse tags JSON for each dish
          items = items.map(dish => {
            let parsedTags = {};
            try {
              parsedTags = typeof dish.tags === 'string' ? JSON.parse(dish.tags) : dish.tags;
              console.log(`[ChefDemo] Parsed tags for ${dish.name}:`, parsedTags);
            } catch (e) {
              console.warn('Failed to parse tags for dish:', dish.id, e);
              parsedTags = {};
            }
            
            let imageUrl = parsedTags.images && parsedTags.images[0] ? parsedTags.images[0].url : null;
            
            // Fallback: if parsing failed, try to extract URL from the raw tags string
            if (!imageUrl && typeof dish.tags === 'string') {
              try {
                const urlMatch = dish.tags.match(/"url":\s*"([^"]+)"/);
                if (urlMatch) {
                  imageUrl = urlMatch[1];
                  console.log(`[ChefDemo] Extracted URL from raw tags for ${dish.name}:`, imageUrl);
                }
              } catch (e) {
                console.warn('Failed to extract URL from raw tags:', e);
              }
            }
            
            console.log(`[ChefDemo] Final image URL for ${dish.name}:`, imageUrl);
            
            return {
              ...dish,
              tags: parsedTags,
              // Add image_url from tags if available
              image_url: imageUrl
            };
          });
        } else {
          console.warn(`Dishes API returned ${res.status}`);
          // Fall back to demo; only flag hard error for 5xx.
          if (res.status >= 500) setHardError((prev) => prev || `Dishes API ${res.status}`);
          items = [];
        }

        let filtered = items.filter((d) => (d.chef_id || d.chefId) === chef.id);
        if (!filtered.length) filtered = buildDemoDishes(chef?.name || "Local Chef", city);
        if (!cancelled) setDishes(filtered.slice(0, 6));
      } catch (e) {
        console.warn("Dishes API fetch failed", e);
        if (!cancelled) setDishes(buildDemoDishes(chef?.name || "Local Chef", city));
      } finally {
        if (!cancelled) setDishLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [chef, city]);

  const specialties = useMemo(() => normalizeSpecialties(chef?.specialties), [chef]);

  const chefPhoto = firstNonEmpty(chef?.photo_url, chef?.photo, PlaceholderChef);
  const chefName  = firstNonEmpty(chef?.name, "Local Chef");
  const chefCity  = firstNonEmpty(chef?.city, city);
  const bio       = firstNonEmpty(chef?.bio, "This chef is currently onboarding. More details coming soon!");

  return (
    <main className="chef-demo">
      <div className="chef-breadcrumb">
        <Link to={`/chefs/${encodeURIComponent(chefCity)}`} className="crumb">
          &larr; Back to {chefCity.charAt(0).toUpperCase() + chefCity.slice(1)} chefs
        </Link>
      </div>

      <section className="chef-hero">
        <div className="chef-photo-wrap">
          <img src={chefPhoto} alt={chefName} className="chef-photo" />
        </div>
        <div className="chef-meta">
          <div className="chef-name-row">
            <h1 className="chef-name">{chefName}</h1>
            {chef?.data_source === 'real' && (
              <span className="data-source-badge">Real Chef</span>
            )}
            {chef?.data_source === 'demo' && (
              <span className="data-source-badge demo">Demo</span>
            )}
          </div>
          <div className="chef-city">Based in <strong>{chefCity.charAt(0).toUpperCase() + chefCity.slice(1)}</strong></div>

          {specialties.length > 0 && (
            <div className="pills">
              {specialties.map((s, i) => <span className="pill" key={`${s}-${i}`}>{s}</span>)}
            </div>
          )}

          <p className="chef-bio">{bio}</p>

          <div className="chef-socials">
            {chef?.website   && <a href={chef.website}   target="_blank" rel="noreferrer">Website</a>}
            {chef?.instagram && <a href={chef.instagram} target="_blank" rel="noreferrer">Instagram</a>}
            {chef?.tiktok    && <a href={chef.tiktok}    target="_blank" rel="noreferrer">TikTok</a>}
            {chef?.youtube   && <a href={chef.youtube}   target="_blank" rel="noreferrer">YouTube</a>}
            {chef?.facebook  && <a href={chef.facebook}  target="_blank" rel="noreferrer">Facebook</a>}
            {chef?.linkedin  && <a href={chef.linkedin}  target="_blank" rel="noreferrer">LinkedIn</a>}
            {chef?.yelp      && <a href={chef.yelp}      target="_blank" rel="noreferrer">Yelp</a>}
            {chef?.opentable && <a href={chef.opentable} target="_blank" rel="noreferrer">OpenTable</a>}
          </div>
        </div>
      </section>

      <section className="dish-section">
        <div className="dish-header">
          <h2>Signature Dishes</h2>
          {!dishLoading && dishes.length === 0 && <p className="muted">Dishes will appear here once this chef completes onboarding.</p>}
        </div>

        <div className="dish-grid">
          {(dishLoading ? Array.from({ length: 3 }) : dishes).map((d, i) => {
            const title = d?.name || "Coming Soon";
            const desc  = d?.description || "This dish will be revealed soon. Stay tuned!";
            const price = d?.price_cents != null ? `$${(d.price_cents / 100).toFixed(2)}` : "";
            const img   = firstNonEmpty(d?.image_url, d?.image?.url, d?.primary_image_url, PlaceholderChef);

            const handleAddToCart = (e) => {
              e.preventDefault();
              e.stopPropagation();
              
              console.log('🛒 Add to cart clicked!');
              console.log('📦 Dish data:', d);
              console.log('📦 Dish ID:', d?.id || i);
              console.log('📦 Title:', title);
              console.log('📦 Price:', d?.price_cents);
              
              try {
                // Get existing cart from localStorage
                const existingCart = JSON.parse(localStorage.getItem('checkout_cart') || '[]');
                console.log('📥 Existing cart:', existingCart);
                
                // Use index as fallback ID if no id exists
                const dishId = d?.id || `temp-${i}`;
                
                // Check if dish already in cart
                const existingItem = existingCart.find(item => item.dish.id === dishId);
                
                let updatedCart;
                if (existingItem) {
                  // Increment quantity
                  updatedCart = existingCart.map(item => 
                    item.dish.id === dishId
                      ? { ...item, quantity: item.quantity + 1 }
                      : item
                  );
                  console.log('📈 Increased quantity for:', title);
                } else {
                  // Add new item
                  updatedCart = [...existingCart, { 
                    dish: {
                      id: dishId,
                      name: title,
                      price_cents: d?.price_cents || 1999,
                      chef_id: chef?.id || idParam,
                      image_url: img
                    }, 
                    quantity: 1 
                  }];
                  console.log('➕ Added new item:', title);
                }
                
              localStorage.setItem('checkout_cart', JSON.stringify(updatedCart));
              console.log('💾 Cart saved, total items:', updatedCart.length);
              console.log('💾 Updated cart:', updatedCart);
              
              // Dispatch custom event to update cart icon
              window.dispatchEvent(new Event('cartUpdated'));
              
              // Show feedback
                const btn = e.currentTarget;
                const originalText = btn.textContent;
                const originalBg = btn.style.background;
                btn.textContent = '✓ Added!';
                btn.style.background = '#27ae60';
                setTimeout(() => {
                  btn.textContent = originalText;
                  btn.style.background = originalBg;
                }, 1500);
                
                alert(`✓ ${title} added to cart!`);
              } catch (error) {
                console.error('❌ Error adding to cart:', error);
                alert('Error adding to cart. Please try again.');
              }
            };

            return (
              <div key={d?.id || i} className="dish-card">
                <div className="dish-photo-wrap">
                  <img src={img} alt={title} className="dish-photo" />
                  <button className="order-now-pill" onClick={handleAddToCart}>
                    Add to Cart
                  </button>
                </div>
                <div className="dish-meta">
                  <div className="dish-title-row">
                    <h3 className="dish-title">{title}</h3>
                    {price && <span className="dish-price">{price}</span>}
                  </div>
                  <p className="dish-desc">{desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Only show a visible error for hard failures (5xx / network) */}
      {hardError && <div className="error">Error: {hardError}</div>}
    </main>
  );
}